import { Fixture, Cup } from '../domain/entities';

export interface ScheduleEntry {
  type: 'cup' | 'league';
  cupName?: string;
  cupRoundIndex?: number; // Index of the cup round (0 = Round 1, etc.)
  leagueFixtures?: { [leagueName: string]: Fixture[] }; // All leagues' fixtures for this week
}

/**
 * Generate season schedule using interleaving algorithm:
 * 1. Build cup rounds list (Junior first, then Pixel)
 * 2. Reverse it (Pixel Final becomes first)
 * 3. Generate per-league fixture weeks
 * 4. Interleave league fixtures with cup rounds (pop from arrays, insert into schedule)
 * 5. Reverse final schedule to get chronological order
 */
export const generateSeasonSchedule = (
  cups: { [cupName: string]: Cup },
  leagueTeamNames: { [leagueName: string]: string[] },
  cupNames: string[]
): {
  fixtures: { [leagueName: string]: Fixture[] };
  cupWeeks: Set<number>;
  cupRoundWeeks: { [cupName: string]: { [roundIndex: number]: number } };
} => {
  // Track cup round weeks for returning to caller
  const cupRoundWeeks: { [cupName: string]: { [roundIndex: number]: number } } = {};
  cupNames.forEach(name => {
    cupRoundWeeks[name] = {};
  });

  // Generate per-league fixture weeks
  // Each entry is an array of all games in that logical week
  const leagueFixturesByWeek: { [leagueName: string]: Fixture[][] } = {};
  Object.entries(leagueTeamNames).forEach(([leagueName, teamNames]) => {
    leagueFixturesByWeek[leagueName] = generateLeagueFixtureWeeks(teamNames);
  });

  // Count total league logical weeks
  let totalLeagueWeeks = 0;
  leagueFixturesByWeek[Object.keys(leagueTeamNames)[0]]?.forEach(() => totalLeagueWeeks++);

  // Calculate how many cup rounds we have total
  let totalCupRounds = 0;
  cupNames.forEach(cupName => {
    const cup = cups[cupName];
    if (cup && cup.rounds) {
      totalCupRounds += cup.rounds.length;
    }
  });

  // Build schedule: allocate weeks for cups and leagues
  // Strategy: Interleave cups and league weeks to spread them throughout the season
  const schedule: ScheduleEntry[] = [];
  const leagueWeekIndices: { [leagueName: string]: number } = {};
  const leagueNames = Object.keys(leagueTeamNames);

  leagueNames.forEach(name => {
    leagueWeekIndices[name] = 0;
  });

  // Create a list of all cup rounds in reverse order
  // We want Pixel Cup Final (first cup, final round) at allCupRounds[0]
  // After schedule.reverse(), allCupRounds[0] gets the highest calendar week
  const allCupRounds: Array<{ cupName: string; roundIndex: number }> = [];
  // Process cups in FORWARD order, but add rounds in REVERSE order
  // This puts Pixel Cup Final first, Junior Cup Final last
  for (let cupIdx = 0; cupIdx < cupNames.length; cupIdx++) {
    const cupName = cupNames[cupIdx];
    const cup = cups[cupName];
    if (cup && cup.rounds) {
      // Add each cup's rounds in reverse order (Final first)
      for (let roundIdx = cup.rounds.length - 1; roundIdx >= 0; roundIdx--) {
        allCupRounds.push({ cupName, roundIndex: roundIdx });
      }
    }
  }

  // Interleave: for each cup round, add some league weeks before it
  const activeLeagueSet = new Set(leagueNames);
  const leagueWeeksPerCupGap = Math.max(1, Math.floor(totalLeagueWeeks / (totalCupRounds + 1)));

  // Add first cup round (Final from last cup in sequence) at the beginning of schedule
  // Since we reversed cupNames and rounded within each cup, allCupRounds[0] is Pixel Cup Final
  // When schedule is reversed at the end, this will be placed at the highest week number
  if (allCupRounds.length > 0) {
    schedule.push({
      type: 'cup',
      cupName: allCupRounds[0].cupName,
      cupRoundIndex: allCupRounds[0].roundIndex
    });
  }

  // Add remaining cup rounds with league weeks interspersed
  for (let cupIdx = 1; cupIdx < allCupRounds.length; cupIdx++) {
    // Add league weeks before this cup round
    let weeksAdded = 0;
    while (weeksAdded < leagueWeeksPerCupGap && activeLeagueSet.size > 0) {
      const leagueFixtures: { [leagueName: string]: Fixture[] } = {};
      const leaguesToRemove: string[] = [];

      for (const leagueName of leagueNames) {
        if (!activeLeagueSet.has(leagueName)) continue;

        const weekIndex = leagueWeekIndices[leagueName];
        const weeks = leagueFixturesByWeek[leagueName];

        if (weekIndex < weeks.length) {
          leagueFixtures[leagueName] = weeks[weekIndex];
          leagueWeekIndices[leagueName]++;
        } else {
          leaguesToRemove.push(leagueName);
        }
      }

      leaguesToRemove.forEach(name => activeLeagueSet.delete(name));

      if (Object.keys(leagueFixtures).length > 0) {
        schedule.push({
          type: 'league',
          leagueFixtures
        });
        weeksAdded++;
      } else {
        break;
      }
    }

    // Add this cup round
    schedule.push({
      type: 'cup',
      cupName: allCupRounds[cupIdx].cupName,
      cupRoundIndex: allCupRounds[cupIdx].roundIndex
    });
  }

  // Add all remaining league weeks
  while (activeLeagueSet.size > 0) {
    const leagueFixtures: { [leagueName: string]: Fixture[] } = {};
    const leaguesToRemove: string[] = [];

    for (const leagueName of leagueNames) {
      if (!activeLeagueSet.has(leagueName)) continue;

      const weekIndex = leagueWeekIndices[leagueName];
      const weeks = leagueFixturesByWeek[leagueName];

      if (weekIndex < weeks.length) {
        leagueFixtures[leagueName] = weeks[weekIndex];
        leagueWeekIndices[leagueName]++;
      } else {
        leaguesToRemove.push(leagueName);
      }
    }

    leaguesToRemove.forEach(name => activeLeagueSet.delete(name));

    if (Object.keys(leagueFixtures).length > 0) {
      schedule.push({
        type: 'league',
        leagueFixtures
      });
    } else {
      break;
    }
  }

  // Reverse the schedule to get chronological order
  // This places Pixel Cup Final (first in reserved list) at the very end
  schedule.reverse();

  // Convert to per-league fixture arrays with calendar weeks
  const fixtures: { [leagueName: string]: Fixture[] } = {};
  const cupWeeks = new Set<number>();

  leagueNames.forEach(name => {
    fixtures[name] = [];
  });

  let calendarWeek = 1;
  schedule.forEach((entry) => {
    if (entry.type === 'cup') {
      cupWeeks.add(calendarWeek);
      // Record the cup round's calendar week
      if (entry.cupName && entry.cupRoundIndex !== undefined) {
        cupRoundWeeks[entry.cupName][entry.cupRoundIndex] = calendarWeek;
      }
      calendarWeek++;
    } else if (entry.type === 'league' && entry.leagueFixtures) {
      // All leagues play in the same calendar week
      for (const [leagueName, leagueFixturesThisWeek] of Object.entries(entry.leagueFixtures)) {
        leagueFixturesThisWeek.forEach(fixture => {
          const updatedFixture = { ...fixture, week: calendarWeek };
          fixtures[leagueName].push(updatedFixture);
        });
      }
      calendarWeek++;
    }
  });

  return { fixtures, cupWeeks, cupRoundWeeks };
};

/**
 * Generate all fixture weeks for a league.
 * Each entry in the returned array contains all games played in that logical week.
 */
function generateLeagueFixtureWeeks(teamNames: string[]): Fixture[][] {
  let teams = [...teamNames];
  const fixturesByWeek: Fixture[][] = [];

  if (teams.length % 2 !== 0) {
    teams.push('BYE');
  }

  const numTeams = teams.length;
  const matchesPerRound = numTeams / 2;

  // Generate all fixtures (both halves)
  const allFixtures: { home: string; away: string; logicalWeek: number }[] = [];

  // First half
  let tempTeams = [...teams];
  for (let week = 0; week < numTeams - 1; week++) {
    for (let match = 0; match < matchesPerRound; match++) {
      const home = tempTeams[match];
      const away = tempTeams[numTeams - 1 - match];
      if (home !== 'BYE' && away !== 'BYE') {
        if (week % 2 === 0) {
          allFixtures.push({ home, away, logicalWeek: week });
        } else {
          allFixtures.push({ home: away, away: home, logicalWeek: week });
        }
      }
    }
    tempTeams.splice(1, 0, tempTeams.pop()!);
  }

  // Second half (reverse fixtures)
  tempTeams = [...teams];
  for (let week = 0; week < numTeams - 1; week++) {
    const secondHalfWeek = (numTeams - 1) + week;
    for (let match = 0; match < matchesPerRound; match++) {
      const home = tempTeams[match];
      const away = tempTeams[numTeams - 1 - match];
      if (home !== 'BYE' && away !== 'BYE') {
        if (week % 2 === 0) {
          allFixtures.push({ home: away, away: home, logicalWeek: secondHalfWeek });
        } else {
          allFixtures.push({ home, away, logicalWeek: secondHalfWeek });
        }
      }
    }
    tempTeams.splice(1, 0, tempTeams.pop()!);
  }

  // Group fixtures by logical week
  const totalWeeks = (numTeams - 1) * 2;
  for (let week = 0; week < totalWeeks; week++) {
    const weekFixtures = allFixtures
      .filter(f => f.logicalWeek === week)
      .map(f => ({ home: f.home, away: f.away, week: week } as Fixture));

    if (weekFixtures.length > 0) {
      fixturesByWeek.push(weekFixtures);
    }
  }

  return fixturesByWeek;
}
